import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect, useId } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { iprjExportTrackingAtom } from '@/atoms/iprjExport-progress.atom';
import type { LayerData } from '@/atoms/layers.atom';
import { AnalysisDialogShell } from '@/components/analyses/AnalysisDialogShell';
import { AnalysisSection, InputRoleField, OutputSummary } from '@/components/analyses/sections';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { execute } from '@/graphql/execute';
import { isImageLayer } from '@/lib/analyses/candidates';
import { useInvalidateAnalysisRuns } from '@/lib/analyses/useAnalysisRuns';
import { useSourceLayerPick } from '@/lib/analyses/useSourceLayerPick';
import { useDialog } from '@/providers/DialogProvider';
import { startIprjExport } from '@/queries/iprjExport.query';
import { getProjectJobsQuery } from '@/queries/project.query';

/** Server-side cap: active IPRJ_EXPORT jobs per organisation. */
const IPRJ_CONCURRENCY_CAP = 2;

const iprjExportSchema = z.object({
  outputName: z.string().min(1),
});
type IprjExportFormData = z.infer<typeof iprjExportSchema>;

const suggestedName = (layer: LayerData | undefined) => `${layer?.name ?? ''} IPRJ`.trim();

const imageCountOf = (layer: LayerData | undefined): number | null => {
  const n = (layer?.metadata as { imageCount?: unknown } | null | undefined)?.imageCount;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

interface Props {
  /** Pre-fill for the source picker (context layer, re-run). D10. */
  sourceLayer?: LayerData;
  projectId: string;
}

/**
 * Packs an IMAGE layer's TopoDOT project files and all its images into a
 * downloadable IPRJ zip (byproduct, no layer). The PRO gate is enforced by
 * the registry / `useLaunchAnalysis` before this opens, and again server-side.
 */
export function RunIprjExportDialog({ sourceLayer: initialSourceLayer, projectId }: Props) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const formId = useId();
  const setTrackers = useSetAtom(iprjExportTrackingAtom);
  const {
    candidates: sourceCandidates,
    sourceLayer,
    sourceLayerId,
    setSourceLayerId,
  } = useSourceLayerPick(isImageLayer, initialSourceLayer);

  // Concurrency cap check: count active IPRJ_EXPORT jobs (same as mesh).
  const { data: jobsData } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
  });
  const activeIprjJobs = (jobsData?.jobs ?? []).filter(
    (j) => j.type === 'IPRJ_EXPORT' && (j.status === 'WAITING' || j.status === 'PROCESSING'),
  );
  const concurrencyCapReached = activeIprjJobs.length >= IPRJ_CONCURRENCY_CAP;

  const form = useForm<IprjExportFormData>({
    resolver: zodResolver(iprjExportSchema),
    defaultValues: { outputName: suggestedName(sourceLayer) },
  });
  const watchedName = useWatch({ control: form.control, name: 'outputName' });

  // Follow the picked layer until the user edits the name.
  useEffect(() => {
    if (!sourceLayer || form.formState.dirtyFields.outputName) return;
    const suggested = suggestedName(sourceLayer);
    if (form.getValues('outputName') !== suggested) form.setValue('outputName', suggested, { shouldDirty: false });
  }, [sourceLayer, form]);

  const imageCount = imageCountOf(sourceLayer);

  const invalidateAnalysisRuns = useInvalidateAnalysisRuns();
  const mutation = useMutation({
    mutationFn: ({ vars, source }: { vars: IprjExportFormData; source: LayerData }) =>
      execute(startIprjExport, {
        input: { sourceLayerId: source.id, name: vars.outputName || undefined },
      }),
    onSuccess: (data, { source }) => {
      invalidateAnalysisRuns(projectId);
      const batchId = data.startIprjExport;
      const toastId = toast.loading(t('iprjExport.runningToast', { name: source.name }), {
        duration: Infinity,
      });
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: source.id,
          sourceLayerName: source.name,
        },
      ]);
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('iprjExport.failedToStart'));
    },
  });

  return (
    <AnalysisDialogShell
      title={t('iprjExport.title')}
      formId={formId}
      primaryLabel={mutation.isPending ? t('iprjExport.starting') : t('iprjExport.run')}
      primaryDisabled={mutation.isPending || concurrencyCapReached || !sourceLayer}
      primaryBusy={mutation.isPending}
      primaryTestId='iprj-export-run'>
      <Form {...form}>
        {/* No height cap here: the shell owns it (ANALYSIS_SHELL_CLASS). */}
        <form
          id={formId}
          onSubmit={form.handleSubmit((vars) => {
            if (!sourceLayer) return;
            mutation.mutate({ vars, source: sourceLayer });
          })}>
          <div className='flex min-w-0 flex-col gap-4 px-1 py-1'>
            <AnalysisSection title={t('analysisConfig.sections.inputs')} testId='iprj-export-section-inputs'>
              <InputRoleField
                id='iprj-export-source-layer'
                testId='iprj-export-source-layer'
                role='asBuilt'
                label={t('analysisConfig.roles.images')}
                emptyMessage={t('analysisLauncher.blocked.needsImageLayer')}
                layers={sourceCandidates}
                value={sourceLayerId}
                onChange={setSourceLayerId}
                disabled={mutation.isPending}
              />
            </AnalysisSection>

            {imageCount !== null && (
              <AnalysisSection title={t('analysisConfig.sections.settings')} testId='iprj-export-section-settings'>
                <p data-testid='iprj-export-image-count' className='text-sm text-slate-700 dark:text-stone-300'>
                  {t('iprjExport.imageCount', { count: imageCount })}
                </p>
              </AnalysisSection>
            )}

            <AnalysisSection title={t('analysisConfig.sections.output')} testId='iprj-export-section-output'>
              <OutputSummary
                testId='iprj-export-output'
                creates={[
                  t('analysisConfig.creates.iprjDownload', {
                    name: watchedName || '…',
                    interpolation: { escapeValue: false },
                  }),
                ]}
                warnings={[t('iprjExport.storageNote'), concurrencyCapReached && t('iprjExport.concurrencyHint')]}>
                <FormField
                  control={form.control}
                  name='outputName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('analysisConfig.resultName')}</FormLabel>
                      <FormControl>
                        <Input type='text' data-testid='iprj-export-name' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </OutputSummary>
            </AnalysisSection>
          </div>
        </form>
      </Form>
    </AnalysisDialogShell>
  );
}
